<svelte:options accessors />

<script lang="ts">
  import { type Header } from "./DataTable.svelte";
  type T = $$Generic;
  type WCSResponse = {
    content: any;
    links: [{ rel: "self"; href: string }];
    page: {
      size: number;
      totalElements: number;
      totalPages: number;
      number: number;
    };
  };
  export let rows: T[], getTableContentFn: (options: any) => Promise<WCSResponse>, headers: Header<T>[];
  export let responseInterceptFn = (response: WCSResponse) => response;
  export let skeleton = false;
  export let skipInit = false;

  import { createEventDispatcher } from "svelte";

  let paginatable = true;
  let sortKey: string;
  let sortDir: "asc" | "desc";
  let pageNumber = 0;
  let hasMore = false;
  const dispatch = createEventDispatcher();

  //TODO: fold into getTableData and deprecate
  export const triggerGetData = async (
    options: any,
    tableReset = true,
    responseInterceptCallback = responseInterceptFn,
  ) => {
    try {
      const response = await getTableData(options, tableReset);
      let { content } = responseInterceptCallback(response);
      rows = content;
    } catch (error) {
      dispatch("error", error);
    }
  };

  const getTableData = async (options: any, tableReset: boolean) => {
    try {
      dispatchFetchStart(tableReset);
      const response = await getTableContentFn(options);
      //
      dispatchFetchComplete(response, tableReset);
      if (response.page) {
        //OBO Dashboard does not conform to known pagination schema.
        let { page } = response;
        handlePageResponse(page);
      }
      if (!tableReset) {
        response.content = [...rows, ...response.content];
        //
      }
      return response;
    } catch (error) {
      dispatch("fetchError", error);
      throw error;
    }

    function dispatchFetchStart(tableReset: boolean) {
      dispatch("fetchStart", tableReset);
      if (tableReset) {
        skeleton = true;
      }
    }

    function dispatchFetchComplete(response: WCSResponse, tableReset: boolean) {
      dispatch("fetchComplete", { response, tableReset });
      skeleton = false;
    }

    function handlePageResponse(page: WCSResponse["page"]) {
      paginatable = !!page.totalPages;
      hasMore = page.totalPages - 1 > page.number;
      pageNumber = page.number + 1;
    }
  };

  export const handleSort = async (e: CustomEvent, options: any) => {
    let eventHeader = e.detail;
    if (!!eventHeader.sort) {
      if (typeof eventHeader.sort === "function" && typeof eventHeader.dir === "undefined") {
        console.warn("Running sort without defined direction; assumes initial sort was desc");
        eventHeader.dir = "asc";
      }
      for (let i = 0; i < headers.length; i++) {
        if (headers[i].key === eventHeader.key) {
          headers[i].dir = eventHeader.dir === "asc" ? "desc" : "asc";
          eventHeader.active = true;
        } else {
          headers[i].active = false;
        }
      }
      sortKey = eventHeader?.sortKey ? eventHeader.sortKey : eventHeader.key; //default sort to header key if no explicit sort key set
      sortDir = eventHeader.dir;
      const hasOverrideSort = !!eventHeader.sort && typeof eventHeader.sort === "function";
      let sortFn = hasOverrideSort
        ? eventHeader.sort
        : (a: number, b: number) => {
            let aVal = a;
            let bVal = b;
            if (aVal === bVal) {
              return 0;
            }
            if (sortDir === "asc") {
              return aVal > bVal ? 1 : -1;
            } else {
              return aVal < bVal ? 1 : -1;
            }
          };

      //sort array of row objects using sortFn
      if (paginatable) {
        options.sortKey = sortKey;
        options.sortDir = sortDir;
        triggerGetData(options, true);
      } else {
        rows.sort((a, b) => sortFn(a[sortKey], b[sortKey]));
        if (hasOverrideSort && sortDir === "desc") {
          rows.reverse();
        }
      }
    }
  };
  let canLoadMore = true;
  export const handleLoadMore = async (
    options: any = {},
    responseInterceptFn: (WCSResponse: WCSResponse) => WCSResponse,
  ) => {
    if (hasMore && canLoadMore) {
      canLoadMore = false;
      options.page = pageNumber;
      options.sortKey = sortKey;
      options.sortDir = sortDir;
      await triggerGetData(options, false, responseInterceptFn);
      canLoadMore = true;
    }
  };

  const init = () => {
    triggerGetData({}, true, responseInterceptFn);
  };

  if (!skipInit) init();
</script>
